import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Check, Trash2, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail, fmtDate } from "../lib/api";

const FIELDS = {
  programmes: [
    ["slug", "Slug *"], ["name_en", "Name (EN) *"], ["name_el", "Name (EL) *"], ["country", "Country (GR/CY/EU) *"],
    ["authority_en", "Authority (EN)"], ["authority_el", "Authority (EL)"], ["legal_basis", "Legal basis"],
    ["funding_type", "Funding type"], ["period", "Period"], ["scope", "Scope (National/Regional/Territorial/EU-wide)"],
    ["territories", "Territories (region ids, comma-sep)", "csv"],
    ["typical_intensity_min", "Typical intensity min %", "num"], ["typical_intensity_max", "Typical intensity max %", "num"],
    ["description_en", "Description (EN)", "ta"], ["description_el", "Description (EL)", "ta"],
    ["source_url", "Source URL *"], ["verified_at", "Verified at *", "date"],
  ],
  calls: [
    ["programme_id", "Programme ID *"], ["identifier", "Call identifier"], ["title_en", "Title (EN) *"], ["title_el", "Title (EL) *"],
    ["status", "Status (open/forthcoming/closed) *"], ["opens_at", "Opens", "date"], ["closes_at", "Closes", "date"],
    ["budget_total", "Budget total", "num"], ["currency", "Currency"],
    ["aid_intensity_min", "Aid intensity min %", "num"], ["aid_intensity_max", "Aid intensity max %", "num"],
    ["project_size_min", "Project size min", "num"], ["project_size_max", "Project size max", "num"],
    ["eligible_entity_sizes", "Eligible entity sizes (comma-sep)", "csv"], ["eligible_regions", "Eligible regions (comma-sep)", "csv"],
    ["eligible_sectors", "Eligible NACE sectors (comma-sep)", "csv"],
    ["co_financing_required", "Co-financing required (yes/no)"], ["de_minimis", "De minimis (yes/no)"],
    ["scoring_criteria", "Scoring criteria (JSON array)", "json"], ["disbursement_profile", "Disbursement profile (JSON)", "json"],
    ["ada_reference", "ΑΔΑ reference"], ["source_url", "Source URL *"], ["verified_at", "Verified at *", "date"], ["verified_by", "Verified by"],
  ],
  regions: [
    ["nuts_code", "NUTS code *"], ["name_en", "Name (EN) *"], ["name_el", "Name (EL) *"], ["country", "Country (GR/CY) *"],
    ["aid_intensity_large", "Aid intensity, large enterprise %", "num"],
    ["valid_from", "Valid from", "date"], ["valid_to", "Valid to", "date"],
    ["notes_en", "Notes (EN)", "ta"], ["notes_el", "Notes (EL)", "ta"], ["geometry_ref", "Geometry ref"],
    ["source_url", "Source URL *"], ["verified_at", "Verified at *", "date"],
  ],
  authorities: [
    ["name_en", "Name (EN) *"], ["name_el", "Name (EL) *"], ["country", "Country (GR/CY/EU) *"],
    ["website", "Website"], ["monitoring_source", "Monitoring source"], ["check_frequency", "Check frequency"],
    ["source_url", "Source URL *"], ["verified_at", "Verified at *", "date"],
  ],
  explorer: [
    ["n", "Name (EN) *"], ["el", "Name (EL) *"], ["c", "Country (GR/CY/EU) *"], ["a", "Authority (EN)"],
    ["t", "Instrument type"], ["b", "Beneficiaries (comma-sep)", "csv"], ["th", "Themes (comma-sep)", "csv"],
    ["i", "Intensity label"], ["s", "Status"], ["lb", "Legal basis"], ["d", "Description (EN)", "ta"],
  ],
};

const ID_FIELDS = { programmes: "programme_id", calls: "fcall_id", regions: "region_id", authorities: "authority_id", explorer: "exp_id" };
const LABELS = { programmes: "Programmes", calls: "Calls", regions: "Regions", authorities: "Authorities", explorer: "Explorer index" };

const toInput = (kind, v) => {
  if (v === null || v === undefined) return "";
  if (kind === "csv") return Array.isArray(v) ? v.join(", ") : String(v);
  if (kind === "json") return typeof v === "string" ? v : JSON.stringify(v);
  return String(v);
};

const fromInput = (kind, v) => {
  const s = String(v || "").trim();
  if (kind === "num") return s === "" ? null : Number(s);
  if (kind === "csv") return s === "" ? [] : s.split(",").map((x) => x.trim()).filter(Boolean);
  if (kind === "json") {
    if (s === "") return null;
    try { return JSON.parse(s); } catch { return s; }
  }
  return s === "" ? null : s;
};

const RecordForm = ({ ftype, initial, onSaved, onClose }) => {
  const fields = FIELDS[ftype];
  const [f, setF] = useState(() => {
    const o = {};
    fields.forEach(([k, , kind]) => { o[k] = toInput(kind, initial[k]); });
    return o;
  });
  const idField = ID_FIELDS[ftype];
  const save = async () => {
    const payload = {};
    fields.forEach(([k, , kind]) => { payload[k] = fromInput(kind, f[k]); });
    try {
      if (initial[idField]) await api.put(`/admin/funding/${ftype}/${initial[idField]}`, payload);
      else await api.post(`/admin/funding/${ftype}`, payload);
      toast.success("Record saved");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="border border-[var(--accent)] rounded-sm p-6 mb-8 bg-paper" data-testid="funding-record-form">
      <h3 className="k-h3 mb-6">{initial[idField] ? "Edit record" : "New record"}</h3>
      <div className="grid md:grid-cols-2 gap-4 mb-5">
        {fields.map(([k, label, kind]) => (
          <div key={k} className={kind === "ta" ? "md:col-span-2" : ""}>
            <label className="field-label">{label}</label>
            {kind === "ta" ? (
              <textarea rows={3} className="field-input" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`funding-field-${k}`} />
            ) : (
              <input type={kind === "date" ? "date" : "text"} className="field-input" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`funding-field-${k}`} />
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-3">
        <button onClick={save} className="btn-primary !py-2 !px-4 text-xs" data-testid="funding-save-btn"><Check size={13} /> Save</button>
        <button onClick={onClose} className="btn-secondary !py-2 !px-4 text-xs" data-testid="funding-cancel-btn">Cancel</button>
      </div>
    </div>
  );
};

export const IngestTab = () => {
  const [items, setItems] = useState([]);
  const [states, setStates] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/ingest/items?status=pending").then(({ data }) => setItems(data));
    api.get("/admin/ingest/state").then(({ data }) => setStates(data));
  }, []);
  useEffect(load, [load]);
  const run = async (source) => {
    toast.info(`Polling ${source}…`);
    const { data } = await api.post(`/admin/ingest/run/${source}`);
    if (data.last_status === "ok") toast.success(`${source}: ${data.last_fetched} fetched, ${data.last_new} new`);
    else toast.error(`${source}: ${data.last_error}`);
    load();
  };
  const review = async (item, status) => {
    await api.put(`/admin/ingest/items/${item.ingest_id}`, { status });
    toast.success(status === "approved" ? "Approved — create the call record in Funding data" : "Dismissed");
    load();
  };
  return (
    <div data-testid="admin-ingest-tab">
      <p className="text-sm text-ink-muted mb-6 max-w-3xl">
        Daily polling of ΔΙΑΥΓΕΙΑ and the EU Funding &amp; Tenders portal (SEDIA), feeding a human review queue. Nothing publishes automatically — approve an item, then create the verified call record under Funding data.
      </p>
      <div className="flex flex-wrap gap-6 mb-8">
        {["diavgeia", "sedia"].map((s) => {
          const st = states.find((x) => x.source === s);
          return (
            <div key={s} className="border border-line p-5 min-w-[260px]" data-testid={`ingest-state-${s}`}>
              <p className="font-semibold uppercase text-xs tracking-widest mb-2">{s === "diavgeia" ? "ΔΙΑΥΓΕΙΑ" : "SEDIA (EU F&T)"}</p>
              <p className="text-xs text-ink-muted mb-3">
                {st ? `Last run ${fmtDate(st.last_run)} · ${st.last_status}${st.last_status === "ok" ? ` · ${st.last_new} new` : ""}` : "Not yet run"}
                {st?.last_error && <span className="block text-red-600 mt-1">{st.last_error.slice(0, 120)}</span>}
              </p>
              <button onClick={() => run(s)} className="btn-primary !py-2 !px-4 text-xs" data-testid={`ingest-run-${s}`}>Run now</button>
            </div>
          );
        })}
      </div>
      <h3 className="k-h3 mb-4">Review queue ({items.length})</h3>
      <div className="border-t border-line">
        {items.map((it) => (
          <div key={it.ingest_id} className="flex flex-wrap items-center gap-4 py-4 border-b border-line" data-testid={`ingest-item-${it.ingest_id}`}>
            <span className="topic-tag">{it.source}</span>
            <div className="flex-1 min-w-[240px]">
              <a href={it.url} target="_blank" rel="noreferrer" className="font-medium text-sm hover:text-brand">{it.title}</a>
              <p className="text-xs text-ink-muted mt-0.5">{it.external_id}{it.issued_at ? ` · ${it.issued_at}` : ""}</p>
            </div>
            <button onClick={() => review(it, "approved")} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`ingest-approve-${it.ingest_id}`}><Check size={13} /> Approve</button>
            <button onClick={() => review(it, "dismissed")} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid={`ingest-dismiss-${it.ingest_id}`}>Dismiss</button>
          </div>
        ))}
        {items.length === 0 && <p className="text-ink-muted py-6 text-sm">Queue is empty. Run a source above to fetch.</p>}
      </div>
    </div>
  );
};

export const FundingTab = () => {
  const [ftype, setFtype] = useState("programmes");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const idField = ID_FIELDS[ftype];
  const load = useCallback(() => {
    api.get(`/admin/funding/${ftype}`).then(({ data }) => setItems(data));
  }, [ftype]);
  useEffect(() => { setEditing(null); load(); }, [load]);
  const del = async (r) => {
    if (!window.confirm(`Delete "${r.name_en || r.title_en || r.nuts_code}"?`)) return;
    await api.delete(`/admin/funding/${ftype}/${r[idField]}`);
    toast.success("Deleted");
    load();
  };
  const title = (r) => r.name_en || r.title_en || r.nuts_code || r[idField];
  const sub = (r) => [r.country, r.slug || r.identifier || r.nuts_code, r.verified_at ? `verified ${r.verified_at}` : null].filter(Boolean).join(" · ");
  return (
    <div data-testid="admin-funding-tab">
      <p className="text-sm text-ink-muted mb-6 max-w-3xl">
        The funding data layer. Every record requires a source URL and a verification date; records past their review interval are flagged stale. Programmes are permanent instruments; calls are the time-bound instances.
      </p>
      <div className="flex gap-2 mb-8 flex-wrap">
        {Object.keys(FIELDS).map((t) => (
          <button key={t} onClick={() => setFtype(t)}
            className={`topic-tag !py-2 !px-4 cursor-pointer border-0 transition-colors ${ftype === t ? "!bg-[var(--accent)] !text-white" : ""}`}
            data-testid={`funding-type-${t}`}>{LABELS[t]}</button>
        ))}
      </div>
      {editing !== null ? (
        <RecordForm ftype={ftype} initial={editing} onSaved={() => { setEditing(null); load(); }} onClose={() => setEditing(null)} />
      ) : (
        <button onClick={() => setEditing({})} className="btn-primary mb-8" data-testid="funding-add-btn"><Plus size={16} /> Add {LABELS[ftype].slice(0, -1).toLowerCase()}</button>
      )}
      <div className="border-t border-line">
        {items.map((r) => (
          <div key={r[idField]} className="flex flex-wrap items-center gap-4 py-4 border-b border-line" data-testid={`funding-row-${r[idField]}`}>
            <div className="flex-1 min-w-[240px]">
              <p className="font-medium">{title(r)} {r.name_el && <span className="text-ink-muted font-normal">· {r.name_el}</span>}</p>
              <p className="text-xs text-ink-muted mt-1">{sub(r)}</p>
            </div>
            {r.stale && (
              <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: "#b03535" }} data-testid={`funding-stale-${r[idField]}`}>
                <AlertTriangle size={13} /> STALE (&gt;{r.review_days}d)
              </span>
            )}
            <button onClick={() => setEditing(r)} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid={`funding-edit-btn-${r[idField]}`}>Edit</button>
            <button onClick={() => del(r)} className="text-ink-muted hover:text-red-600 bg-transparent border-0 cursor-pointer" title="Delete" data-testid={`funding-delete-btn-${r[idField]}`}><Trash2 size={15} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-ink-muted py-6 text-sm">No records yet.</p>}
      </div>
    </div>
  );
};
